// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Typography,
  withStyles,
} from "@material-ui/core";
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from '@material-ui/icons/Search';
import { FormTextField, TreeViewNav } from "../../../components";
import { getApisListResults, getApisListTotalPages } from "../../../reducers/products";
import { fetchApis } from "../../../actions/products";
import compose from "recompose/compose";
import styles from "./styles";
import { connect } from "react-redux";
import { isEmpty, upperCase } from "lodash";
import { object, arrayOf, bool, func, string, number } from 'prop-types';
import { ASSIGNED } from "../../../constants";
import { getI18n } from "../../../utils/intl";
import { useIntl } from "react-intl";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

export const ProductApiTree = (props) => {

  const {
    classes,
    onSelectEntity,
    getTreeSectionItems,
    handleSelectDocument,
    setExpanded,
    handleDocumentParentChange,
    selectedDocumentId,
    expanded,
    userCanEdit = false,
    idPrefix,
    apisList=[],
    fetchApis,
    parentEntityUuid,
    apiListTotalPages,
    isDocTreeLoading,
    setShowEntityOverview,
    tabValue,
  } = props;

  const [filterByApiName, setFilterByApiName] = useState("");
  const [pageNum, setPageNum] = useState(0);
  const [panelExpanded, setPanelExpanded] = useState([]);
  const [hasProductApis, setHasProductApis] = useState(false);
  const pageSize = 5;
  const intl = getI18n(useIntl());
  const entityType = "api";
  const pageId = `${idPrefix}-doc-api-section`;

  useEffect(() => {
    if ((tabValue === idPrefix + '-documentation-tab') && parentEntityUuid) {
      fetchApiList();
    }
  },[parentEntityUuid, pageNum, tabValue])

  useEffect(() => {
    if(apisList.length > 0) {
      setHasProductApis(true);
    }
  }, [apisList])

  const onFilterByApiNameKeyPress = (e) => {
    if(e.key === 'Enter') { fetchApiList() }
  }

  const onFilterByApiNameKeyChange = (value) => {
    setFilterByApiName(value);
    if(isEmpty(value)) { fetchApiList() }
  }

  const fetchApiList = () => {
    fetchApis({
      uuid : parentEntityUuid,
      name : filterByApiName,
      page : pageNum,
      size : pageSize,
      assign : ASSIGNED,
    })
  }

  const onClickNext = () => setPageNum(pageNum + 1);
  const onClickPrev = () => setPageNum(pageNum - 1);
  const isPrevDisabled = pageNum === 0;
  const isNextDisabled = pageNum + 1 === apiListTotalPages;

  const handlePanelExpansion = (api, isExpanded) => {
    if(isExpanded && !panelExpanded.find(id => id === api.apiUuid)) {
      setPanelExpanded([...panelExpanded, api.apiUuid])
    } else {
      setPanelExpanded([...panelExpanded.filter(id => id !== api.apiUuid)])
    }
    onSelectEntity({ type : entityType, uuid : api.apiUuid })
    setShowEntityOverview(true);
  }

  const isPanelExpanded = (uuid) => (panelExpanded.indexOf(uuid) !== -1)

  return (<>
    { hasProductApis &&
      <>
        <Divider/>
        <div className={classes.expandPanelToolbar}>
          <Typography
            variant='h5'
            className={classes.sectionTitle}
            id={`${pageId}-title`}
            data-apim-test={`${pageId}-title`}>
            {intl.getI18nMessage("label.documentation.section.title", { entityType : upperCase(entityType) })}
          </Typography>
          <FormTextField
            id={`${pageId}-search`}
            data-apim-test={`${pageId}-search`}
            fieldContainerClass={classes.filterByApiNameContainer}
            handleChange={onFilterByApiNameKeyChange}
            onKeyPress={onFilterByApiNameKeyPress}
            placeholder={intl.getI18nMessage("label.documentation.section.search.by.name",
              { entityType : upperCase(entityType) },
            )}
            value={filterByApiName}
            suffixIcon={<SearchIcon color={"primary"} onClick={fetchApiList}/>}
            textFieldClass={classes.searchFieldClass}
          />
        </div>
        {
          apisList.map(api =>
            <>
              <Divider/>
              <ExpansionPanel
                id={`${pageId}-expansion-panel`}
                data-apim-test={`${pageId}-expansion-panel`}
                classes={{ root : classes.panelRoot }}
                key={api.apiUuid}
                onChange={(event, expanded) => handlePanelExpansion(api, expanded)}
                expanded={isPanelExpanded(api.apiUuid)}
              >
                <ExpansionPanelSummary
                  classes={{
                    root : classes.summaryRoot,
                    content: classes.summaryContent,
                }}
                >
                  { isPanelExpanded(api.apiUuid) ?
                    <ExpandLessIcon color={"primary"}/> :
                    <ExpandMoreIcon color={"primary"}/>
                  }
                  <Typography
                    id={`${pageId}-expansion-panel-summary-title`}
                    data-apim-test={`${pageId}-expansion-panel-summary-title`}
                    className={classes.summaryTitle}
                  >
                    {api.name}
                  </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails
                  id={`${pageId}-expansion-panel-details`}
                  data-apim-test={`${pageId}-expansion-panel-details`}
                  className={classes.expandPanelDetails}
                >
                  {
                    <div className={classes.expandPanelDetailsContent}>
                      {
                        (() => {
                          const treeItems =
                            getTreeSectionItems({ type: entityType, uuid: api.apiUuid });
                          return treeItems.length > 0 ?
                            <TreeViewNav
                              items={treeItems}
                              onDocumentSelected={handleSelectDocument}
                              selectedDocumentId={selectedDocumentId}
                              expanded={expanded}
                              onExpandedChange={setExpanded}
                              onDocumentParentChange={handleDocumentParentChange}
                              canDrag={userCanEdit}
                              id={`${pageId}-tree-view`}
                            />
                            :
                            <>
                              {!isDocTreeLoading &&
                                <Typography className={classes.noDocContainer}>
                                  {intl.getI18nMessage("label.documentation.section.no.docs",
                                    { entityType : upperCase(entityType) })}
                                </Typography>
                              }
                            </>
                        })()
                      }
                    </div>
                  }
                </ExpansionPanelDetails>
              </ExpansionPanel>
            </>,
          )
        }
        {
          apiListTotalPages > 1 && <div className={classes.paginationContainer}>
            <Button
              id={`${pageId}-pagination-prev-button`}
              data-apim-test={`${pageId}-pagination-prev-button`}
              startIcon={<KeyboardArrowLeftIcon/>}
              color={"primary"}
              onClick={onClickPrev}
              disabled={isPrevDisabled}
            >
              {intl.getI18nMessage("label.pagination.prev")}
            </Button>
            <Button
              id={`${pageId}-pagination-next-button`}
              data-apim-test={`${pageId}-pagination-next-button`}
              endIcon={<KeyboardArrowRightIcon/>}
              color={"primary"}
              onClick={onClickNext}
              disabled={isNextDisabled}
            >
              {intl.getI18nMessage("label.pagination.next")}
            </Button>
          </div>
        }
      </>
    }
  </>)
}

const mapStateToProps = state => ({
  apisList: getApisListResults(state),
  apiListTotalPages: getApisListTotalPages(state),
});

const mapDispatchToProps = {
  fetchApis,
}

ProductApiTree.propTypes = {
  classes: object,
  handlePanelExpansion: func,
  isPanelExpanded: bool,
  getTreeSectionItems: func,
  handleSelectDocument: func,
  setExpanded: func,
  handleDocumentParentChange: func,
  selectedDocumentId: string,
  expanded: bool,
  userCanEdit : bool,
  idPrefix : string,
  apisList : arrayOf(object),
  fetchApis : func,
  parentEntityUuid : string,
  apiListTotalPages : number,
  isDocTreeLoading : bool,
  onSelectEntity : func,
  setShowEntityOverview : func,
  tabValue : string,
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ProductApiTree);
